import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Heart, MessageCircle, Bookmark, Send, X,
  ArrowLeft, MoreVertical, Trash2, Flag, AlertTriangle,
  Edit2, Smile
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useAuth } from './AuthContext.jsx';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { useSmartBack } from '../utils/useSmartBack.js';
import { localizeMessage } from '../utils/toast.js';
const API_BASE_URL = `${GREENWICH_BACKEND_URL}/api`;
const BASE_URL     = `${GREENWICH_BACKEND_URL}`;

// ── Same helpers as Feed.jsx ─────────────────────────────────────────────
const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
};

const getImageUrl = (imagePath) => {
  if (!imagePath || imagePath === 'null' || imagePath === 'undefined')
    return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.includes('uploads') || imagePath.includes('nkarner')) {
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${BASE_URL}${cleanPath}`;
  }
  return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
};

const formatDate = (date) => {
  if (!date) return '';
  try {
    const d    = new Date(date);
    if (isNaN(d.getTime())) return '';
    const now  = new Date();
    const diff = now - d;
    const m    = Math.floor(diff / 60000);
    const h    = Math.floor(diff / 3600000);
    const day  = Math.floor(diff / 86400000);
    if (m  < 1)  return 'Just now';
    if (m  < 60) return `${m}m ago`;
    if (h  < 24) return `${h}h ago`;
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  const headers = {
    'Authorization': `Bearer ${token}`,
    ...(options.body && typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers
  };
  const res = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }
  return res.json();
};

// ── ImageCarousel — exact copy from Feed.jsx ────────────────────────────
const ImageCarousel = ({ images = [] }) => {
  const [idx,     setIdx]     = useState(0);
  const [hovered, setHovered] = useState(false);
  if (!images || images.length === 0) return null;
  const goToPrev = (e) => { e?.stopPropagation(); setIdx(p => p === 0 ? images.length - 1 : p - 1); };
  const goToNext = (e) => { e?.stopPropagation(); setIdx(p => p === images.length - 1 ? 0 : p + 1); };
  return (
    <div className="image-carousel-container" style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="carousel-image-wrapper" style={{ cursor: 'pointer', position: 'relative' }}>
        <img src={getImageUrl(images[idx])} alt={`Image ${idx + 1}`}
          style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain', backgroundColor: '#f5f5f5', borderRadius: '8px' }}
          onError={handleImageError} />
      </div>
      {images.length > 1 && (
        <>
          <div className="carousel-controls"
            style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)',
              display: hovered ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', pointerEvents: 'none', zIndex: 2 }}>
            <button className="carousel-btn" onClick={goToPrev} style={{ pointerEvents: 'auto', marginLeft: '5px' }}>{'<'}</button>
            <button className="carousel-btn" onClick={goToNext} style={{ pointerEvents: 'auto', marginRight: '5px' }}>{'>'}</button>
          </div>
          <div className="carousel-dots"
            style={{ position: 'absolute', bottom: '20px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '12px', zIndex: 2 }}>
            {images.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`carousel-dot ${i === idx ? 'active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── PostOptions — exact copy from Feed.jsx ───────────────────────────────
const PostOptions = ({ post, onDelete, onReport, currentUserId }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const isOwner = post.author?._id === currentUserId;
  return (
    <div className="post-options-container" ref={ref}>
      <span className="post-options-icon" onClick={(e) => { e.stopPropagation(); setShow(!show); }} style={{ cursor: 'pointer' }}>
        <MoreVertical size={20} />
      </span>
      {show && (
        <div className="post-options-menu">
          {isOwner && (
            <div className="post-option delete" onClick={(e) => { e.stopPropagation(); onDelete && onDelete(post._id); setShow(false); }}>
              <Trash2 size={16} /><span>Delete Post</span>
            </div>
          )}
          <div className="post-option report" onClick={(e) => { e.stopPropagation(); onReport && onReport(post._id); setShow(false); }}>
            <Flag size={16} /><span>Report Post</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── CommentItem — exact copy from Feed.jsx ───────────────────────────────
const CommentItem = ({ comment, depth = 0, onLike, onReport, onDeleteClick,
  currentUserId, postId, replyInputs, setReplyInputs,
  handleCommentReply, activeEmojiComment, setActiveEmojiComment }) => {
  const [showOpts, setShowOpts] = useState(false);
  const [liking,   setLiking]   = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowOpts(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const isLiked = comment.likes?.includes(currentUserId) || false;
  const handleLike = async () => {
    if (liking) return;
    setLiking(true);
    try { await onLike(comment._id); } catch {} finally { setLiking(false); }
  };
  const handleReplyClick = () => {
    const name = comment.author?.name || 'User';
    setReplyInputs({ ...replyInputs, [comment._id]: `@${name} ` });
    setTimeout(() => { const el = document.querySelector(`.reply-input[data-comment-id="${comment._id}"]`); if (el) el.focus(); }, 100);
  };
  return (
    <div className="comment-item" style={{ marginLeft: depth > 0 ? '32px' : '0', marginTop: depth > 0 ? '8px' : '0', position: 'relative' }}>
      {depth > 0 && <div className="reply-connector" style={{ position: 'absolute', left: '-16px', top: '24px', bottom: 0, width: '2px', backgroundColor: '#e0e0e0' }} />}
      <div className="comment" style={{ display: 'flex', gap: '8px' }}>
        <img src={getImageUrl(comment.author?.profileImage)} alt={comment.author?.name} className="comment-avatar"
          style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} onError={handleImageError} />
        <div className="comment-content" style={{ flex: 1 }}>
          <div className="comment-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span className="comment-author-name" style={{ fontWeight: '600', fontSize: '14px' }}>{comment.author?.name || 'Unknown'}</span>
            <span className="comment-time" style={{ fontSize: '12px', color: '#65676b' }}>{formatDate(comment.createdAt)}</span>
          </div>
          <p className="comment-text" style={{ margin: '4px 0', fontSize: '14px', lineHeight: '1.4' }}>{comment.content || ''}</p>
          <div className="comment-actions" style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
            <button className={`comment-action-btn like-btn ${isLiked ? 'liked' : ''}`} onClick={handleLike} disabled={liking}
              style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '12px', color: isLiked ? '#ef4444' : '#65676b' }}>
              <Heart size={12} fill={isLiked ? '#ef4444' : 'none'} color={isLiked ? '#ef4444' : '#65676b'} />
              <span>{comment.likes?.length || 0}</span>
            </button>
            {depth < 10 && (
              <button className="comment-action-btn reply-btn" onClick={handleReplyClick}
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '12px', color: '#65676b' }}>
                <span>Reply</span>
              </button>
            )}
            <div className="comment-options-container" ref={ref} style={{ position: 'relative' }}>
              <button className="comment-action-btn more-btn" onClick={() => setShowOpts(!showOpts)}
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', padding: '2px 4px', cursor: 'pointer', color: '#65676b' }}>
                <MoreVertical size={12} />
              </button>
              {showOpts && (
                <div className="comment-options-menu" style={{ position: 'absolute', top: '100%', right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '150px' }}>
                  <button className="comment-option" onClick={() => { onReport && onReport(comment._id); setShowOpts(false); }}
                    style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#65676b' }}>
                    <Flag size={14} /><span>Report</span>
                  </button>
                  {comment.author?._id === currentUserId && (
                    <button className="comment-option delete" onClick={() => { onDeleteClick && onDeleteClick(comment._id, postId); setShowOpts(false); }}
                      style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#ef4444' }}>
                      <Trash2 size={14} /><span>Delete</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {replyInputs[comment._id] !== undefined && (
            <div className="reply-input-container" style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
              <div className="reply-input-wrapper" style={{ flex: 1, position: 'relative' }}>
                <input type="text" placeholder="Write a reply..."
                  value={replyInputs[comment._id] || ''}
                  onChange={(e) => setReplyInputs({ ...replyInputs, [comment._id]: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleCommentReply(comment._id, postId)}
                  className="reply-input" data-comment-id={comment._id}
                  style={{ width: '100%', padding: '8px 12px', paddingRight: '36px', border: '1px solid #e0e0e0', borderRadius: '20px', fontSize: '13px' }} />
                <button onClick={() => setActiveEmojiComment(activeEmojiComment === comment._id ? null : comment._id)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#65676b' }}>
                  <Smile size={16} />
                </button>
              </div>
              <button onClick={() => handleCommentReply(comment._id, postId)} disabled={!replyInputs[comment._id]?.trim()}
                style={{ background: '#32614d', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: replyInputs[comment._id]?.trim() ? 1 : 0.5 }}>
                <Send size={14} />
              </button>
              {activeEmojiComment === comment._id && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 1000 }}>
                  <EmojiPicker onEmojiClick={(d) => { setReplyInputs({ ...replyInputs, [comment._id]: (replyInputs[comment._id] || '') + d.emoji }); setActiveEmojiComment(null); }}
                    disableAutoFocus native height={350} width={300} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Modals ────────────────────────────────────────────────────────────────
const DeletePostModal = ({ isOpen, onClose, onDelete, postId }) => {
  const [del, setDel] = useState(false);
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>Delete Post</h3><button className="spv-modal-close" onClick={onClose}><X size={20} /></button></div>
        <div className="modal-body"><div className="delete-warning"><X size={48} style={{ color: '#ef4444', marginBottom: '16px' }} /><h4>Are you sure?</h4><p>This post will be permanently deleted.</p></div></div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={del}>Cancel</button>
          <button className="delete-btn" disabled={del} onClick={async () => { setDel(true); try { await onDelete(postId); onClose(); } catch {} finally { setDel(false); } }}>
            {del ? <div className="mini-spinner"></div> : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteCommentModal = ({ isOpen, onClose, onDelete, commentId }) => {
  const [del, setDel] = useState(false);
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>Delete Comment</h3><button className="spv-modal-close" onClick={onClose}><X size={20} /></button></div>
        <div className="modal-body"><div className="delete-warning"><X size={48} style={{ color: '#ef4444', marginBottom: '16px' }} /><h4>Delete this comment?</h4></div></div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={del}>Cancel</button>
          <button className="delete-btn" disabled={del} onClick={async () => { setDel(true); try { await onDelete(commentId); onClose(); } catch {} finally { setDel(false); } }}>
            {del ? <div className="mini-spinner"></div> : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportModal = ({ isOpen, onClose, onReport, type, targetId }) => {
  const [sel, setSel] = useState('');
  const [det, setDet] = useState('');
  const [rep, setRep] = useState(false);
  if (!isOpen) return null;
  const reasons = ['Spam', 'Inappropriate Content', 'Harassment', 'Hate Speech', 'Misinformation', 'Other'];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><AlertTriangle size={20} /><h3>Report {type === 'post' ? 'Post' : 'Comment'}</h3><button className="spv-modal-close" onClick={onClose}><X size={20} /></button></div>
        <div className="modal-body">
          <div className="report-reasons">
            {reasons.map(r => (
              <div key={r} className={`report-reason ${sel === r ? 'selected' : ''}`} onClick={() => setSel(r)}>
                <div className="reason-radio">{sel === r && <div className="radio-dot"></div>}</div>
                <div className="reason-content"><div className="reason-label">{r}</div></div>
              </div>
            ))}
          </div>
          <div className="report-details"><textarea placeholder="Additional details (optional)" value={det} onChange={(e) => setDet(e.target.value)} rows={3} /></div>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={rep}>Cancel</button>
          <button className="report-btn" disabled={!sel || rep} onClick={async () => { setRep(true); try { await onReport({ type, targetId, reason: sel, details: det }); onClose(); } catch {} finally { setRep(false); } }}>
            {rep ? <div className="mini-spinner"></div> : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────
export default function SinglePostView() {
  const { postId } = useParams();
  const navigate   = useNavigate();
  const goBack = useSmartBack('/feed');
  const { user }   = useAuth();

  const [post,           setPost]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLiking,       setIsLiking]       = useState(false);
  const [isSaving,       setIsSaving]       = useState(false);
  const [isCommenting,   setIsCommenting]   = useState(false);
  const [commentInput,   setCommentInput]   = useState('');
  const [replyInputs,    setReplyInputs]    = useState({});
  const [activeEmojiComment, setActiveEmojiComment] = useState(null);
  const [showDeleteModal,        setShowDeleteModal]        = useState(false);
  const [showReportModal,        setShowReportModal]        = useState(false);
  const [reportTarget,           setReportTarget]           = useState({ type: '', id: '' });
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [commentToDelete,        setCommentToDelete]        = useState(null);

  const userId = user?._id || user?.id || null;

  useEffect(() => {
    if (!postId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await apiRequest(`/posts/${postId}`);
        const p    = data.post || data;
        const author = p.author || p.user || {};
        const normalized = {
          _id: p._id || p.id,
          content: p.content || '',
          images: p.images || (p.image ? [p.image] : []),
          createdAt: p.createdAt || new Date().toISOString(),
          author: {
            _id:              author._id || author.id || 'unknown',
            organizationName: author.organizationName || author.name || 'Unknown User',
            name:             author.name || author.username || 'Unknown',
            profileImage:     author.profileImage || '',
            userType:         author.userType || 'player',
            isVerified:       author.isVerified || false,
          },
          likes:    Array.isArray(p.likes)    ? p.likes    : [],
          saves:    Array.isArray(p.saves)    ? p.saves    : [],
          comments: Array.isArray(p.comments) ? p.comments : [],
        };
        setPost(normalized);
        try {
          const cr   = await apiRequest(`/posts/${postId}/comments`);
          const cmts = Array.isArray(cr) ? cr : cr.comments || [];
          if (cmts.length > 0) setPost(prev => prev ? { ...prev, comments: cmts } : prev);
        } catch {}
      } catch (err) {
        setError(err.message || 'Failed to load post.');
      } finally {
        setLoading(false);
      }
    })();
  }, [postId]);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(null), 3000); return () => clearTimeout(t); }
  }, [successMessage]);

  const isLiked     = () => post?.likes?.some(l => (typeof l === 'string' ? l : l._id) === userId) || false;
  const isPostSaved = () => post?.saves?.some(s => (typeof s === 'string' ? s : s._id) === userId) || false;

  const handleLike = async () => {
    if (!userId || isLiking) return;
    const was = isLiked();
    setIsLiking(true);
    setPost(p => ({ ...p, likes: was ? p.likes.filter(l => (typeof l === 'string' ? l : l._id) !== userId) : [...p.likes, userId] }));
    try { await apiRequest(`/posts/${postId}/like`, { method: 'POST' }); }
    catch { setPost(p => ({ ...p, likes: was ? [...p.likes, userId] : p.likes.filter(l => (typeof l === 'string' ? l : l._id) !== userId) })); }
    finally { setIsLiking(false); }
  };

  const handleSave = async () => {
    if (!userId || isSaving) return;
    const was = isPostSaved();
    setIsSaving(true);
    setPost(p => ({ ...p, saves: was ? p.saves.filter(s => (typeof s === 'string' ? s : s._id) !== userId) : [...p.saves, userId] }));
    try { await apiRequest(`/posts/${postId}/save`, { method: 'POST', body: JSON.stringify({ action: was ? 'unsave' : 'save' }) }); }
    catch {}
    finally { setIsSaving(false); }
  };

  const handleComment = async (parentCommentId = null, content = null) => {
    if (!userId) return;
    const text = content || commentInput.trim();
    if (!text) return;
    setIsCommenting(true);
    const tempId = `temp_${Date.now()}`;
    const newC   = { _id: tempId, content: text, parentCommentId: parentCommentId || null,
      author: { _id: userId, name: user?.name || 'Me', profileImage: user?.profileImage },
      createdAt: new Date().toISOString(), likes: [] };
    setPost(p => ({ ...p, comments: [newC, ...(p.comments || [])] }));
    if (!content) setCommentInput('');
    try {
      const res = await apiRequest(`/posts/${postId}/comments`, { method: 'POST', body: JSON.stringify({ content: text, parentCommentId: parentCommentId || null }) });
      if (res.comment) setPost(p => ({ ...p, comments: p.comments.map(c => c._id === tempId ? res.comment : c) }));
      setSuccessMessage(parentCommentId ? 'Reply posted!' : 'Comment added!');
      if (parentCommentId) setReplyInputs(prev => { const n = { ...prev }; delete n[parentCommentId]; return n; });
    } catch {
      setPost(p => ({ ...p, comments: p.comments.filter(c => c._id !== tempId) }));
      setError('Failed to add comment.');
    } finally { setIsCommenting(false); }
  };

  const handleCommentReply  = async (cId) => { const t = replyInputs[cId]?.trim(); if (t) await handleComment(cId, t); };
  const handleCommentLike   = async (cId) => {
    if (!userId) return;
    setPost(p => ({ ...p, comments: p.comments.map(c => {
      if ((c._id || c.id) !== cId) return c;
      const liked = c.likes?.includes(userId);
      return { ...c, likes: liked ? (c.likes || []).filter(id => id !== userId) : [...(c.likes || []), userId] };
    })}));
    try { await apiRequest(`/comments/${cId}/like`, { method: 'POST' }); } catch {}
  };
  const handleDeletePost    = async (pId) => { await apiRequest(`/posts/${pId}`, { method: 'DELETE' }); setPost(null); setSuccessMessage('Post deleted.'); setTimeout(goBack, 1000); };
  const handleDeleteComment = async (cId) => { setPost(p => ({ ...p, comments: p.comments.filter(c => (c._id || c.id) !== cId) })); try { await apiRequest(`/comments/${cId}`, { method: 'DELETE' }); } catch {} setSuccessMessage('Comment deleted.'); };
  const handleReport        = async (data) => { try { await apiRequest('/reports', { method: 'POST', body: JSON.stringify(data) }); setSuccessMessage('Report submitted.'); } catch {} };

  const buildCommentTree = (comments) => {
    if (!comments || !Array.isArray(comments)) return [];
    const map  = {};
    const root = [];
    comments.forEach(c => { if (c?._id) map[c._id] = { ...c, children: [] }; });
    comments.forEach(c => { if (c?._id) { if (c.parentCommentId && map[c.parentCommentId]) map[c.parentCommentId].children.push(map[c._id]); else root.push(map[c._id]); } });
    return root;
  };

  const renderCommentTree = (comments, depth = 0) => {
    if (!comments || comments.length === 0) return null;
    return comments.map(comment => (
      <React.Fragment key={`comment-${comment._id}`}>
        <CommentItem comment={comment} depth={depth}
          onLike={handleCommentLike}
          onReport={(cId) => { setReportTarget({ type: 'comment', id: cId }); setShowReportModal(true); }}
          onDeleteClick={(cId) => { setCommentToDelete(cId); setShowDeleteCommentModal(true); }}
          currentUserId={userId} postId={postId}
          replyInputs={replyInputs} setReplyInputs={setReplyInputs}
          handleCommentReply={handleCommentReply}
          activeEmojiComment={activeEmojiComment} setActiveEmojiComment={setActiveEmojiComment}
        />
        {comment.children?.length > 0 && <div className="comment-children">{renderCommentTree(comment.children, depth + 1)}</div>}
      </React.Fragment>
    ));
  };

  if (loading) return (
    <div className="feed-container" style={{ justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="loading-spinner"></div>
    </div>
  );

  if (error || !post) return (
    <div className="feed-container" style={{ justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16 }}>
      <p style={{ color: '#65676b', fontSize: 15 }}>{error || 'Post not found.'}</p>
      <button className="cancel-btn" onClick={goBack}>Go Back</button>
    </div>
  );

  const commentTree = buildCommentTree(post.comments);

  return (
    <div className="feed-container">
      <div className="feed-main" style={{ maxWidth: 680, margin: '0 auto', width: '100%' }}>

        {/* Back */}
        <button onClick={goBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'white', border: '1px solid rgba(50,97,77,.18)', borderRadius: 10, color: '#32614d', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 16 }}>
          <ArrowLeft size={15} /> Back
        </button>

        {error          && <div className="error-message">{localizeMessage(error)}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* ── Post card — identical to Feed ── */}
        <div className="post-card">

          {/* Header */}
          <div className="post-header">
            <img src={getImageUrl(post.author?.profileImage)} alt={post.author?.name}
              className="post-author-avatar"
              onClick={() => post.author?._id && navigate(`/profile/${post.author._id}`)}
              style={{ cursor: 'pointer' }} onError={handleImageError} />
            <div className="post-author-info" onClick={() => post.author?._id && navigate(`/profile/${post.author._id}`)} style={{ cursor: 'pointer' }}>
              <div className="post-author-name">{post.author?.organizationName || post.author?.name || 'Unknown User'}</div>
              <div className="post-meta"><span className="post-time">{formatDate(post.createdAt)}</span></div>
            </div>
            <PostOptions post={post}
              onDelete={() => setShowDeleteModal(true)}
              onReport={(pId) => { setReportTarget({ type: 'post', id: pId }); setShowReportModal(true); }}
              currentUserId={userId} />
          </div>

          {/* Content */}
          <div className="post-content">
            <p>{post.content || ''}</p>
            {post.images?.length > 0 && (
              <div className="post-images-carousel" style={{ marginTop: '12px' }}>
                <ImageCarousel images={post.images} />
              </div>
            )}
          </div>

          {/* Actions — identical to Feed */}
          <div className="post-actions">
            <button className={`action-button like-button ${isLiked() ? 'liked' : ''}`} onClick={handleLike} disabled={isLiking}>
              <div className="action-button-content">
                {isLiking ? <div className="mini-spinner"></div> : <Heart size={20} fill={isLiked() ? '#32614d' : 'none'} stroke="#32614d" />}
                <span className="action-count">{post.likes?.length || 0}</span>
              </div>
            </button>

            <button className="action-button comment-button">
              <div className="action-button-content">
                <MessageCircle size={20} stroke="#32614d" />
                <span className="action-count">{post.comments?.length || 0}</span>
              </div>
            </button>

            <button className={`action-button save-button ${isPostSaved() ? 'saved' : ''}`} onClick={handleSave} disabled={isSaving}>
              <div className="action-button-content">
                {isSaving ? <div className="mini-spinner"></div> : <Bookmark size={20} fill={isPostSaved() ? '#32614d' : 'none'} stroke="#32614d" />}
                <span className="action-count">{post.saves?.length || 0}</span>
              </div>
            </button>

            <button className="action-button send-button"
              onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/post/${postId}`); setSuccessMessage('Link copied!'); }}>
              <div className="action-button-content">
                <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt="Share"
                  style={{ width: '20px', height: '20px', transform: 'rotate(30deg)' }} />
                <span className="action-count">Share</span>
              </div>
            </button>
          </div>

          {/* Comments — always visible (no toggle) */}
          <div className="post-comments-section">
            <div className="comments-list">
              {commentTree.length === 0 ? (
                <div className="no-comments">
                  <MessageCircle size={32} style={{ color: '#ccc' }} />
                  <p>No comments yet</p>
                  <small>Be the first to comment!</small>
                </div>
              ) : renderCommentTree(commentTree)}
            </div>

            {/* Comment input */}
            <div className="add-comment">
              <div className="comment-input-wrapper" style={{ position: 'relative', flex: 1 }}>
                <input type="text" placeholder="Write a comment..."
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                  className="comment-input"
                  style={{ width: '100%', padding: '8px 12px', paddingRight: '36px', border: '1px solid #e0e0e0', borderRadius: '20px' }} />
                <button onClick={() => setActiveEmojiComment(activeEmojiComment === postId ? null : postId)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#65676b' }}>
                  <Smile size={16} />
                </button>
                {activeEmojiComment === postId && (
                  <div style={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 1000 }}>
                    <EmojiPicker onEmojiClick={(d) => { setCommentInput(t => t + d.emoji); setActiveEmojiComment(null); }}
                      disableAutoFocus native height={350} width={300} />
                  </div>
                )}
              </div>
              <button className="comment-submit-btn" onClick={() => handleComment()} disabled={!commentInput.trim() || isCommenting}
                style={{ background: '#32614d', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: commentInput.trim() && !isCommenting ? 1 : 0.5 }}>
                {isCommenting ? <div className="mini-spinner"></div> : <Send size={16} color="white" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DeletePostModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onDelete={handleDeletePost} postId={postId} />
      <DeleteCommentModal isOpen={showDeleteCommentModal} onClose={() => { setShowDeleteCommentModal(false); setCommentToDelete(null); }} onDelete={handleDeleteComment} commentId={commentToDelete} />
      <ReportModal isOpen={showReportModal} onClose={() => { setShowReportModal(false); setReportTarget({ type: '', id: '' }); }} onReport={handleReport} type={reportTarget.type} targetId={reportTarget.id} />
    </div>
  );
}